/**
 * Spatial hash — a 2D grid over the XZ plane for collision broadphase.
 *
 * Ported from v1 `src/client/physics/SpatialHash.js`, with two changes that
 * matter:
 *
 * 1. v1's grid was module-level singleton state. Two worlds in one process —
 *    which is exactly what a server hosting several matches is — would have
 *    shared one grid and corrupted each other. It is an instance now.
 * 2. v1 inserted every entity at its centre point only. A 90x90 ground plane
 *    therefore lived in one 8x8 cell, and a player standing 30 units away
 *    queried three cells that did not contain it and fell through the floor.
 *    Boxes are registered into every cell they overlap.
 *
 * Query order is deterministic: cells are visited in a fixed dx/dz order and
 * each cell holds an array in insertion order, so two runtimes that inserted
 * the same map see identical candidate lists.
 */
/** Larger than the biggest entity in v1 (~6 units on hex_a_gone). */
export const CELL_SIZE = 8;
/**
 * A box spanning more cells than this is registered as "always a candidate"
 * rather than smeared across thousands of cells. One arena-sized floor plane
 * costs a single extra distance test per query; ten thousand map entries cost
 * memory forever.
 */
const MAX_CELLS_PER_BOX = 1024;
function cellKey(cx, cz) {
    return (cx << 16) ^ cz;
}
export class SpatialHash {
    cells = new Map();
    memberCells = new Map();
    always = [];
    /** `member` is an index into whatever array the caller is indexing. */
    insertPoint(member, x, z) {
        this.insertBox(member, x, z, 0, 0);
    }
    insertBox(member, x, z, hx, hz) {
        const minCx = Math.floor((x - hx) / CELL_SIZE);
        const maxCx = Math.floor((x + hx) / CELL_SIZE);
        const minCz = Math.floor((z - hz) / CELL_SIZE);
        const maxCz = Math.floor((z + hz) / CELL_SIZE);
        if ((maxCx - minCx + 1) * (maxCz - minCz + 1) > MAX_CELLS_PER_BOX) {
            this.always.push(member);
            this.memberCells.set(member, []);
            return;
        }
        const keys = [];
        for (let cx = minCx; cx <= maxCx; cx++) {
            for (let cz = minCz; cz <= maxCz; cz++) {
                const key = cellKey(cx, cz);
                keys.push(key);
                const cell = this.cells.get(key);
                if (cell)
                    cell.push(member);
                else
                    this.cells.set(key, [member]);
            }
        }
        this.memberCells.set(member, keys);
    }
    remove(member) {
        const keys = this.memberCells.get(member);
        if (keys === undefined)
            return;
        this.memberCells.delete(member);
        for (const key of keys) {
            const cell = this.cells.get(key);
            if (!cell)
                continue;
            const i = cell.indexOf(member);
            if (i >= 0)
                cell.splice(i, 1);
            if (cell.length === 0)
                this.cells.delete(key);
        }
        const ai = this.always.indexOf(member);
        if (ai >= 0)
            this.always.splice(ai, 1);
    }
    update(member, x, z, hx = 0, hz = 0) {
        this.remove(member);
        this.insertBox(member, x, z, hx, hz);
    }
    clear() {
        this.cells.clear();
        this.memberCells.clear();
        this.always.length = 0;
    }
    /**
     * Fills `out` with every member in the 3x3 cell neighbourhood (~24x24 units)
     * and returns it. Writing into a caller-owned array keeps the hot path
     * allocation-free without v1's shared-buffer footgun, where two overlapping
     * queries silently clobbered each other.
     *
     * A member overlapping several of the visited cells appears once per cell;
     * callers must tolerate duplicates. Collision resolution is idempotent per
     * candidate, so this is cheaper than de-duplicating.
     */
    queryInto(x, z, out) {
        out.length = 0;
        for (let i = 0; i < this.always.length; i++) {
            const m = this.always[i];
            if (m !== undefined)
                out.push(m);
        }
        const cx = Math.floor(x / CELL_SIZE);
        const cz = Math.floor(z / CELL_SIZE);
        for (let dx = -1; dx <= 1; dx++) {
            for (let dz = -1; dz <= 1; dz++) {
                const cell = this.cells.get(cellKey(cx + dx, cz + dz));
                if (!cell)
                    continue;
                for (let i = 0; i < cell.length; i++) {
                    const m = cell[i];
                    if (m !== undefined)
                        out.push(m);
                }
            }
        }
        return out;
    }
    query(x, z) {
        return this.queryInto(x, z, []);
    }
}
/** Builds the broadphase index for a static platform list. */
export function buildPlatformHash(platforms) {
    const hash = new SpatialHash();
    for (let i = 0; i < platforms.length; i++) {
        const p = platforms[i];
        if (!p)
            continue;
        hash.insertBox(i, p.x, p.z, p.hx, p.hz);
    }
    return hash;
}
//# sourceMappingURL=spatialHash.js.map